import { useMemo, useEffect, useState } from 'react'
import { studentsStore } from '../../../pages/Students/model/studentsStore'
import { ensureStudentsLoaded } from '../../../pages/Students/model/ensureStudentsLoaded'
import { applicationsStore } from '../../../pages/ApplicationsPage/model/applicationsStore'
import { fetchApplications } from '../../../api/applications'
import { fetchTasks } from '../../../api/task'
import * as flags from 'country-flag-icons/react/3x2'
import {
	getCountryNameByIso2,
	getIso2FromCountryName,
} from '../../../shared/utils/countryNameByIso2'

const parseDateSafe = value => {
	if (!value) return null

	if (value instanceof Date) {
		return Number.isNaN(value.getTime()) ? null : value
	}

	if (typeof value === 'number') {
		const dt = new Date(value)
		return Number.isNaN(dt.getTime()) ? null : dt
	}

	const s = String(value).trim()
	if (!s) return null

	const isoDay = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s)
	if (isoDay) {
		const y = Number(isoDay[1])
		const m = Number(isoDay[2]) - 1
		const d = Number(isoDay[3])
		const local = new Date(y, m, d, 0, 0, 0, 0)
		return Number.isNaN(local.getTime()) ? null : local
	}

	const dt = new Date(s)
	return Number.isNaN(dt.getTime()) ? null : dt
}

const normalizeStatus = value => {
	const key = String(value || 'not_started')
		.trim()
		.toLowerCase()
		.replace(/\s+/g, '_')
		.replace(/-/g, '_')

	if (key === 'completed' || key === 'done') return 'completed'
	if (key === 'in_progress' || key === 'pending') return 'in_progress'
	return 'not_started'
}

const calculateDaysRemaining = dueDate => {
	if (!dueDate) return null

	const today = new Date()
	today.setHours(0, 0, 0, 0)

	const due = new Date(dueDate)
	due.setHours(0, 0, 0, 0)

	const diffTime = due.getTime() - today.getTime()
	return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
}

const formatDaysLeft = days => {
	if (days === null || days === undefined) return ''
	if (days < 0) {
		const abs = Math.abs(days)
		return `${abs} ${abs === 1 ? 'day' : 'days'} overdue`
	}
	if (days === 0) return 'Due today'
	if (days === 1) return '1 day left'
	return `${days} days left`
}

const getStatusLabel = status =>
	status === 'in_progress' ? 'In Progress' : 'Not Started'

export const useMessageStudentInfo = ({ studentId }) => {
	const [isLoading, setIsLoading] = useState(true)
	const [isLoadingAssignments, setIsLoadingAssignments] = useState(true)
	const [assignments, setAssignments] = useState([])

	useEffect(() => {
		let alive = true
		setAssignments([])
		setIsLoadingAssignments(true)

		const load = async () => {
			try {
				await ensureStudentsLoaded()

				if (!applicationsStore.applications?.length) {
					try {
						const response = await fetchApplications()
						const applications = response.applications || []
						applicationsStore.setApplications(applications)
					} catch (appErr) {
						console.warn('Failed to load applications:', appErr)
					}
				}

				if (studentId) {
					try {
						const response = await fetchTasks()
						const allTasks = response.tasks || []
						const studentTasks = allTasks
							.filter(task => String(task.studentId) === String(studentId))
							.filter(task => {
								const status = normalizeStatus(
									task.student_status || task.status,
								)
								return status === 'not_started' || status === 'in_progress'
							})
							.map(task => {
								const status = normalizeStatus(
									task.student_status || task.status,
								)
								const dueDate = parseDateSafe(
									task.due_date || task.dueDate || task.due,
								)
								const daysRemaining = calculateDaysRemaining(dueDate)

								return {
									id: task.id,
									title: task.task || task.title || task.name,
									status,
									statusLabel: getStatusLabel(status),
									daysLeftLabel: formatDaysLeft(daysRemaining),
									isUrgent: daysRemaining !== null && daysRemaining <= 3,
								}
							})
						if (alive) setAssignments(studentTasks)
					} catch (err) {
						console.warn('Failed to load tasks:', err)
						if (alive) setAssignments([])
					}
				}
			} catch (e) {
				console.error('Failed to load student data:', e)
			} finally {
				if (alive) setIsLoading(false)
				setIsLoadingAssignments(false)
			}
		}

		load()

		return () => {
			alive = false
		}
	}, [studentId])

	const student = useMemo(() => {
		if (!studentId || !studentsStore.students?.length) return null
		return (
			studentsStore.students.find(s => String(s.id) === String(studentId)) ||
			null
		)
		// eslint-disable-next-line react-hooks/exhaustive-deps -- MobX store reactivity
	}, [studentId, studentsStore.students])

	const {
		name,
		grade,
		profile_completion_score,
		target_countries,
		dream_country,
		career,
		category,
		sub_category,
	} = student || {}

	const countriesList = useMemo(() => {
		if (Array.isArray(target_countries) && target_countries.length > 0) {
			return target_countries
		}
		if (dream_country) {
			if (Array.isArray(dream_country)) {
				return dream_country
			}
			return [dream_country]
		}
		return []
	}, [target_countries, dream_country])

	const countryList = useMemo(() => {
		return countriesList.map(countryName => {
			const iso2 = getIso2FromCountryName(countryName)
			const FlagIcon = iso2 ? flags[iso2] : null
			const countryNameDisplay = iso2 ? getCountryNameByIso2(iso2) : countryName

			return {
				name: countryNameDisplay,
				iso2,
				FlagIcon,
			}
		})
	}, [countriesList])

	const universitiesList = useMemo(() => {
		if (!studentId) return []
		const studentApplications = applicationsStore.applications.filter(
			app => String(app.studentId) === String(studentId),
		)
		const uniqueUniversities = [
			...new Set(
				studentApplications.map(app => app.university).filter(Boolean),
			),
		]
		return uniqueUniversities
		// eslint-disable-next-line react-hooks/exhaustive-deps -- MobX store reactivity
	}, [studentId, applicationsStore.applications])

	const careersList = useMemo(() => {
		const careers = []
		if (career) careers.push(career)
		if (category && category !== career) careers.push(category)
		if (sub_category && sub_category !== career && sub_category !== category) {
			careers.push(sub_category)
		}
		return [...new Set(careers.filter(Boolean))]
	}, [career, category, sub_category])

	const profileScore = Math.round(profile_completion_score || 0)

	return {
		student,
		name: name || '',
		grade: grade || '',
		profileScore,
		countryList,
		universitiesList,
		careersList,
		assignments,
		isLoading,
		isLoadingAssignments,
	}
}
